import { useState } from 'react';
import {
  Card, Select, Group, Button, Table, Loader, Center, Text, Modal, Stack,
  TextInput, ActionIcon, Badge, Tabs, Divider, Chip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IconPlus, IconTrash, IconCalendar, IconUsersGroup } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { TEACHER_PARAMS } from '../lib/roles';
import { useAcademicYears, type ClassOpt } from '../hooks/useLookups';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { Specialty } from '../components/Specialty';
import { confirmDelete } from '../lib/confirm';
import { tzDowIso } from '../lib/format';
import { CreatableSelect, QuickSubjectForm, QuickTeacherForm } from '../components/QuickCreate';
import { useAuth } from '../contexts/AuthContext';
import { canAccess } from '../lib/permissions';

/** Kun filtrida "barcha kunlar" (butun hafta) qiymati. */
const ALL_DAYS = 'all';

interface SubjectOpt { id: string; name: string }
interface TeacherOpt { id: string; fullName: string }
interface ScheduleEntry {
  id: string; dayOfWeek: number; lessonNo?: number | null; startTime: string; endTime: string; room?: string;
  subject?: { name: string }; teacher?: { fullName: string; specialty?: string | null } | null;
}
interface Assignment {
  id: string; teacher?: { fullName: string; specialty?: string | null }; subject?: { name: string }; class?: { name: string };
}

export default function Schedule() {
  const { t } = useTranslation();
  const DAYS = ['', t('days.1'), t('days.2'), t('days.3'), t('days.4'), t('days.5'), t('days.6'), t('days.7')];
  const qc = useQueryClient();
  const { user } = useAuth();
  // Barcha yozuv amallari va /staff qidiruvi backendда STAFF-only — faqat shu rollar boshqara oladi.
  const canManage = canAccess(user?.role, 'staff');
  const [classId, setClassId] = useState<string | null>(null);
  // O'quv yili tanlovi — jadvalni KELGUSI chorak/yil uchun OLDINDAN kiritish yo'li.
  // Jadval sinfga, sinf esa o'quv yiliga bog'langan; haftalik jadval muddatsiz amal
  // qiladi. Demak kelgusi davr jadvali = o'sha davr (yil) sinflarini tanlab kiritish.
  // Ilgari tanlagich BARCHA yillarning sinflarini aralash ko'rsatardi — "5-A" (2025)
  // bilan "5-A" (2026) farqlanmay, oldindan kiritish amalda imkonsiz edi.
  // null = foydalanuvchi hali tanlamagan, sukutda faol yil olinadi.
  const [yearId, setYearId] = useState<string | null>(null);
  // Kun filtri: '1'..'7' (Dush..Yak) yoki ALL_DAYS. Sukut — BUGUNGI kun (Toshkent vaqti
  // bo'yicha, brauzer mintaqasidan mustaqil): sahifa ochilishi bilan aynan bugungi darslar.
  const [day, setDay] = useState<string>(() => String(tzDowIso()));
  const [lessonModal, lessonHandlers] = useDisclosure(false);
  const [assignModal, assignHandlers] = useDisclosure(false);

  const { data: years, isPending: yearsPending } = useAcademicYears();
  // Sukut — faol yil (yo'q bo'lsa ro'yxatdagi birinchisi): oddiy kunda foydalanuvchi
  // yil haqida o'ylamaydi, rejalashtirishda esa kelgusi yilni o'zi tanlaydi.
  const effectiveYearId = yearId ?? years?.find((y) => y.isActive)?.id ?? years?.[0]?.id ?? null;
  const selectedYear = years?.find((y) => y.id === effectiveYearId);
  // `useClasses` o'rniga yilga bog'liq so'rov — kesh kaliti ham yil bilan farqlanadi.
  // Yillar so'rovi TUGAGUNCHA kutiladi (isPending): aks holda avval "hamma sinf" chiqib,
  // filtr qo'llangach ro'yxat "sakrab" o'zgarardi. Yillar bo'lmasa yoki so'rov xato
  // bersa — filtrsiz (eski xatti-harakat), sahifa ishlayverishi muhim.
  const { data: classes } = useQuery({
    queryKey: ['classes-of-year', effectiveYearId, user?.id],
    queryFn: async () =>
      (await api.get<ClassOpt[]>('/classes', {
        params: effectiveYearId ? { academicYearId: effectiveYearId } : {},
      })).data,
    enabled: !yearsPending,
  });
  const { data: subjects } = useQuery({ queryKey: ['subjects'], queryFn: async () => (await api.get<SubjectOpt[]>('/subjects')).data });
  /* DARS VAQTLARI — serverdan. Hisobni panelda takrorlasak, sozlama
     o'zgarganda ikkisi ajralib qolardi va tanlangan "3-soat" saqlangan
     vaqtdan farq qilardi. */
  const { data: darsVaqt } = useQuery({
    queryKey: ['lesson-times'],
    queryFn: async () =>
      (await api.get<{ lessonStart: string; lessonMinutes: number; breakMinutes: number;
                       darslar: { no: number; startTime: string; endTime: string }[] }>(
        '/schedule/lesson-times')).data,
  });

  const { data: teachers } = useQuery({ queryKey: ['teachers'], queryFn: async () => (await api.get<TeacherOpt[]>('/staff', { params: TEACHER_PARAMS })).data, enabled: canManage });

  const { data: schedule, isLoading } = useQuery({
    queryKey: ['schedule', classId, day],
    queryFn: async () =>
      (await api.get<ScheduleEntry[]>('/schedule', {
        params: { classId, ...(day === ALL_DAYS ? {} : { dayOfWeek: day }) },
      })).data,
    enabled: !!classId,
    // Kun almashganda jadval "yo'qolib" turmasin — yangi kun kelguncha eskisi ko'rinadi.
    placeholderData: keepPreviousData,
  });
  const { data: assignments } = useQuery({
    queryKey: ['assignments', classId],
    queryFn: async () => (await api.get<Assignment[]>('/assignments', { params: { classId } })).data,
    enabled: !!classId,
  });

  const lessonForm = useForm({
    initialValues: { subjectId: '', teacherId: '', dayOfWeek: '1', lessonNo: '1', room: '' },
    validate: { subjectId: (v) => (!v ? t('schedulePage.selectSubject') : null) },
  });
  const assignForm = useForm({
    initialValues: { teacherId: '', subjectId: '' },
    validate: { teacherId: (v) => (!v ? t('schedulePage.selectTeacher') : null), subjectId: (v) => (!v ? t('schedulePage.selectSubject') : null) },
  });

  const addLesson = useMutation({
    mutationFn: (v: typeof lessonForm.values) => api.post('/schedule', { ...v, classId, dayOfWeek: Number(v.dayOfWeek) }),
    onSuccess: (_res, v) => {
      notifications.show({ color: 'green', message: t('schedulePage.lessonAdded') });
      // Boshqa kunga dars qo'shilsa filtr o'sha kunga o'tadi — aks holda yangi dars
      // ko'rinmay qolib, "qo'shilmadi" degan taassurot berardi.
      if (day !== ALL_DAYS && day !== v.dayOfWeek) setDay(v.dayOfWeek);
      qc.invalidateQueries({ queryKey: ['schedule'] });
      lessonForm.reset();
      lessonHandlers.close();
    },
  });
  const delLesson = useMutation({
    mutationFn: (id: string) => api.delete(`/schedule/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedule'] }),
  });
  const addAssign = useMutation({
    mutationFn: (v: typeof assignForm.values) => api.post('/assignments', { ...v, classId }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('schedulePage.assigned') }); qc.invalidateQueries({ queryKey: ['assignments'] }); assignForm.reset(); assignHandlers.close(); },
  });
  const delAssign = useMutation({
    mutationFn: (id: string) => api.delete(`/assignments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assignments'] }),
  });

  return (
    <>
      <PageHeader title={t('pages.schedule.title')} />

      <Card mb="md">
        <Group align="flex-end" gap="md">
          {/* Yil tanlagichi faqat yillar mavjud bo'lganda — yilsiz eski (filtrsiz) rejim */}
          {!!years?.length && (
            <Select
              label={t('classesPage.academicYear')}
              data={years.map((y) => ({ value: y.id, label: y.isActive ? `${y.name} ✓` : y.name }))}
              value={effectiveYearId}
              onChange={(v) => {
                if (!v || v === effectiveYearId) return;
                setYearId(v);
                // Tanlangan sinf boshqa yilniki bo'lib qolmasin — yil almashsa tozalanadi
                setClassId(null);
              }}
              allowDeselect={false}
              w={180}
            />
          )}
          <Select
            label={t('fields.class')}
            placeholder={t('common.selectPlaceholder')}
            data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
            value={classId}
            onChange={setClassId}
            searchable
            w={220}
          />
        </Group>
        {/* Faol bo'lmagan yil tanlanganida ochiq eslatma: foydalanuvchi KELGUSI davr
            jadvalini kiritayotganini bilib tursin — aks holda "joriy jadval buzildi"
            degan xavotir tug'ilardi. */}
        {!!selectedYear && !selectedYear.isActive && (
          <Text c="orange" fz="sm" mt="xs">{t('schedulePage.planningNotice', { year: selectedYear.name })}</Text>
        )}
        {/* Tanlangan (odatda kelgusi) yilda hali sinf yo'q — nima qilish kerakligi aytiladi */}
        {!!effectiveYearId && classes?.length === 0 && (
          <Text c="dimmed" fz="sm" mt="xs">{t('schedulePage.noClassesInYear')}</Text>
        )}
      </Card>

      {!classId ? (
        <Center h={140}><Text c="dimmed">{t('schedulePage.selectClassToStart')}</Text></Center>
      ) : (
        <Tabs defaultValue="schedule">
          <Tabs.List mb="md">
            <Tabs.Tab value="schedule" leftSection={<IconCalendar size={16} />}>{t('schedulePage.scheduleTab')}</Tabs.Tab>
            <Tabs.Tab value="assign" leftSection={<IconUsersGroup size={16} />}>{t('schedulePage.assignTab')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="schedule">
            {/* Kun filtri — "Hammasi" (butun hafta) + 7 kun; sukut bo'yicha bugungi kun */}
            <Chip.Group value={day} onChange={(v) => setDay((v as string) || ALL_DAYS)}>
              <Group gap={6} mb="sm" wrap="wrap">
                <Chip value={ALL_DAYS} size="xs" variant="light">{t('schedulePage.allDays')}</Chip>
                {DAYS.slice(1).map((d, i) => (
                  <Chip key={i + 1} value={String(i + 1)} size="xs" variant="light">{d}</Chip>
                ))}
              </Group>
            </Chip.Group>
            {/* Karta doim ko'rinadi — ichida yuklanish/bo'sh holat/jadval almashadi */}
            <SectionCard
              title={t('schedulePage.scheduleTab')}
              icon={IconCalendar}
              p={0}
              right={canManage
                ? (
                  <Button
                    size="xs"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => {
                      // Tanlangan kun modalda oldindan tanlangan bo'ladi — ortiqcha klik yo'q
                      if (day !== ALL_DAYS) lessonForm.setFieldValue('dayOfWeek', day);
                      lessonHandlers.open();
                    }}
                  >
                    {t('schedulePage.addLesson')}
                  </Button>
                )
                : undefined}
            >
              {isLoading ? (
                <Center h={120}><Loader /></Center>
              ) : !schedule?.length ? (
                <Center h={120}><Text c="dimmed">{t(day === ALL_DAYS ? 'schedulePage.emptySchedule' : 'schedulePage.emptyDay')}</Text></Center>
              ) : (
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr>
                      {/* Bitta kun tanlanganda "Kun" ustuni ortiqcha — takrorlanadi */}
                      {day === ALL_DAYS && <Table.Th>{t('schedulePage.day')}</Table.Th>}
                      <Table.Th>{t('schedulePage.time')}</Table.Th>
                      <Table.Th>{t('fields.subject')}</Table.Th>
                      <Table.Th>{t('roles.TEACHER')}</Table.Th>
                      <Table.Th>{t('schedulePage.room')}</Table.Th>
                      {canManage && <Table.Th w={50}></Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {schedule.map((e) => (
                      <Table.Tr key={e.id}>
                        {day === ALL_DAYS && <Table.Td><Badge variant="light">{DAYS[e.dayOfWeek]}</Badge></Table.Td>}
                        <Table.Td>
                          {e.lessonNo != null && (
                            <Badge size="sm" variant="light" mr={6}>{e.lessonNo}-soat</Badge>
                          )}
                          {e.startTime}–{e.endTime}
                        </Table.Td>
                        <Table.Td fw={500}>{e.subject?.name}</Table.Td>
                        <Table.Td>{e.teacher ? <>{e.teacher.fullName}<Specialty value={e.teacher.specialty} /></> : '—'}</Table.Td>
                        <Table.Td>{e.room ?? '—'}</Table.Td>
                        {canManage && <Table.Td><ActionIcon color="red" variant="subtle" onClick={() => confirmDelete(e.subject?.name ?? t('schedulePage.lesson'), () => delLesson.mutate(e.id))} loading={delLesson.isPending}><IconTrash size={16} /></ActionIcon></Table.Td>}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </SectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="assign">
            {/* Qo'shish tugmasi sarlavha qatorida — karta bo'sh holatda ham ko'rinadi */}
            <SectionCard
              title={t('schedulePage.assignTab')}
              icon={IconUsersGroup}
              p={0}
              right={canManage
                ? <Button size="xs" leftSection={<IconPlus size={16} />} onClick={assignHandlers.open}>{t('schedulePage.assign')}</Button>
                : undefined}
            >
              {!assignments?.length ? (
                <Center h={120}><Text c="dimmed">{t('schedulePage.noAssignments')}</Text></Center>
              ) : (
                <Table verticalSpacing="sm">
                  <Table.Thead>
                    <Table.Tr><Table.Th>{t('roles.TEACHER')}</Table.Th><Table.Th>{t('fields.subject')}</Table.Th>{canManage && <Table.Th w={50}></Table.Th>}</Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {assignments.map((a) => (
                      <Table.Tr key={a.id}>
                        <Table.Td fw={500}>{a.teacher?.fullName}<Specialty value={a.teacher?.specialty} /></Table.Td>
                        <Table.Td>{a.subject?.name}</Table.Td>
                        {canManage && <Table.Td><ActionIcon color="red" variant="subtle" onClick={() => confirmDelete(`${a.teacher?.fullName} — ${a.subject?.name}`, () => delAssign.mutate(a.id))} loading={delAssign.isPending}><IconTrash size={16} /></ActionIcon></Table.Td>}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </SectionCard>
          </Tabs.Panel>
        </Tabs>
      )}

      {/* Dars qo'shish modal */}
      <Modal opened={lessonModal} onClose={lessonHandlers.close} title={t('schedulePage.addLesson')} centered>
        <form onSubmit={lessonForm.onSubmit((v) => addLesson.mutate(v))}>
          <Stack>
            <CreatableSelect
              label={t('fields.subject')} required
              data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={lessonForm.values.subjectId}
              onChange={(v) => lessonForm.setFieldValue('subjectId', v || '')}
              error={lessonForm.errors.subjectId}
              createTitle={t('pages.subjects.add')}
              renderCreate={(onCreated) => <QuickSubjectForm onCreated={onCreated} />}
            />
            <CreatableSelect
              label={t('roles.TEACHER')}
              data={(teachers ?? []).map((tt) => ({ value: tt.id, label: tt.fullName }))}
              value={lessonForm.values.teacherId}
              onChange={(v) => lessonForm.setFieldValue('teacherId', v || '')}
              createTitle={t('qc.newTeacher')}
              renderCreate={(onCreated) => <QuickTeacherForm onCreated={onCreated} />}
            />
            <Select label={t('schedulePage.day')} data={DAYS.slice(1).map((d, i) => ({ value: String(i + 1), label: d }))} {...lessonForm.getInputProps('dayOfWeek')} />
            {/* VAQT QO'LDA KIRITILMAYDI — faqat dars raqami. Vaqt maktab
                sozlamasidan kelib chiqadi va serverda hisoblanadi, shuning
                uchun ikki dars ustma-ust tushib qolmaydi. */}
            <Select
              label={t('schedulePage.lessonNo')}
              description={t('schedulePage.lessonNoHint')}
              data={(darsVaqt?.darslar ?? []).map((d) => ({
                value: String(d.no),
                label: `${d.no}-soat  (${d.startTime}–${d.endTime})`,
              }))}
              allowDeselect={false}
              {...lessonForm.getInputProps('lessonNo')}
            />
            <TextInput label={t('schedulePage.room')} {...lessonForm.getInputProps('room')} />
            <Group justify="flex-end"><Button variant="default" onClick={lessonHandlers.close}>{t('common.cancel')}</Button><Button type="submit" loading={addLesson.isPending}>{t('common.save')}</Button></Group>
          </Stack>
        </form>
      </Modal>

      {/* Biriktirish modal */}
      <Modal opened={assignModal} onClose={assignHandlers.close} title={t('schedulePage.assignTitle')} centered>
        <form onSubmit={assignForm.onSubmit((v) => addAssign.mutate(v))}>
          <Stack>
            <CreatableSelect
              label={t('roles.TEACHER')} required
              data={(teachers ?? []).map((tt) => ({ value: tt.id, label: tt.fullName }))}
              value={assignForm.values.teacherId}
              onChange={(v) => assignForm.setFieldValue('teacherId', v || '')}
              error={assignForm.errors.teacherId}
              createTitle={t('qc.newTeacher')}
              renderCreate={(onCreated) => <QuickTeacherForm onCreated={onCreated} />}
            />
            <CreatableSelect
              label={t('fields.subject')} required
              data={(subjects ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={assignForm.values.subjectId}
              onChange={(v) => assignForm.setFieldValue('subjectId', v || '')}
              error={assignForm.errors.subjectId}
              createTitle={t('pages.subjects.add')}
              renderCreate={(onCreated) => <QuickSubjectForm onCreated={onCreated} />}
            />
            <Divider />
            <Group justify="flex-end"><Button variant="default" onClick={assignHandlers.close}>{t('common.cancel')}</Button><Button type="submit" loading={addAssign.isPending}>{t('schedulePage.assign')}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
